import { useState } from "react";
import { CalendarOffIcon } from "lucide-react";

import { DashboardScheduleSkeleton } from "./DashboardScheduleSkeleton";
import { ScheduleCalendarExpanded } from "./ScheduleCalendarExpanded";
import { ScheduleCalendarMini } from "./ScheduleCalendarMini";
import { ScheduleCalendarRail } from "./ScheduleCalendarRail";
import { ScreeningAvailabilitySheet } from "./ScreeningAvailabilitySheet";

import { Center } from "@/components/Center";
import { Column } from "@/components/Column";
import { Text } from "@/components/Text";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  DashboardInterview,
  DashboardSchedulePanelView,
  DashboardTask,
} from "@/types";

// Single entry point for the 7-day schedule everywhere it renders
export const ScheduleCalendar = ({
  calendarExpanded,
  interviews,
  isPending,
  isError,
  onSelectCandidacy,
  showOwnerAvatar = false,
  panelView,
  onPanelViewChange,
  weekStart,
  tasks,
  onPrevWeek,
  onNextWeek,
  onOpenTasksView,
}: {
  // undefined means we'll use a toggle from inside component
  calendarExpanded?: boolean;
  interviews: DashboardInterview[];
  isPending: boolean;
  isError: boolean;
  onSelectCandidacy: (interview: DashboardInterview) => void;
  showOwnerAvatar?: boolean;
  panelView?: DashboardSchedulePanelView;
  onPanelViewChange?: (view: DashboardSchedulePanelView) => void;
  weekStart?: Date;
  tasks?: DashboardTask[];
  onPrevWeek?: () => void;
  onNextWeek?: () => void;
  onOpenTasksView?: (day: Date) => void;
}) => {
  // Only meaningful when calendarExpanded is undefined (job pipeline)
  const [railOpenedToMini, setRailOpenedToMini] = useState(false);

  const [availabilityOpen, setAvailabilityOpen] = useState(false);

  const handleEditAvailability = () => {
    mixpanelUtils.trackButtonClick(
      "Open screen availability",
      calendarExpanded === undefined
        ? MIXPANEL_SCREENS.JOB
        : MIXPANEL_SCREENS.DASHBOARD,
    );
    setAvailabilityOpen(true);
  };

  const variant: "rail" | "mini" | "expanded" =
    calendarExpanded === true
      ? "expanded"
      : calendarExpanded === false || railOpenedToMini
        ? "mini"
        : "rail";

  const handleExpandFromRail = () => {
    mixpanelUtils.trackButtonClick("Expand schedule", MIXPANEL_SCREENS.JOB);
    setRailOpenedToMini(true);
  };

  const handleCollapseToRail = () => {
    mixpanelUtils.trackButtonClick("Collapse schedule", MIXPANEL_SCREENS.JOB);
    setRailOpenedToMini(false);
  };

  const render = () => {
    if (isPending) return <DashboardScheduleSkeleton variant={variant} />;
    if (isError) return <CalendarPanelError />;

    if (
      variant === "expanded" &&
      weekStart &&
      tasks &&
      panelView &&
      onPanelViewChange &&
      onPrevWeek &&
      onNextWeek &&
      onOpenTasksView
    ) {
      return (
        <ScheduleCalendarExpanded
          weekStart={weekStart}
          interviews={interviews}
          tasks={tasks}
          panelView={panelView}
          onPanelViewChange={onPanelViewChange}
          onEditAvailability={handleEditAvailability}
          onPrevWeek={onPrevWeek}
          onNextWeek={onNextWeek}
          onOpenTasksView={onOpenTasksView}
          showInterviewOwnerAvatars={showOwnerAvatar}
          onSelectCandidacy={onSelectCandidacy}
        />
      );
    }

    if (variant === "mini") {
      return (
        <ScheduleCalendarMini
          interviews={interviews}
          panelView={panelView}
          onPanelViewChange={onPanelViewChange}
          onEditAvailability={handleEditAvailability}
          onSelectCandidacy={onSelectCandidacy}
          onCollapseToRail={
            calendarExpanded === undefined ? handleCollapseToRail : undefined
          }
          showOwnerAvatar={showOwnerAvatar}
        />
      );
    }

    return (
      <ScheduleCalendarRail
        interviews={interviews}
        onExpand={handleExpandFromRail}
      />
    );
  };

  const renderSheet = () => (
    <ScreeningAvailabilitySheet
      open={availabilityOpen}
      onOpenChange={setAvailabilityOpen}
    />
  );

  if (calendarExpanded !== undefined) {
    return (
      <>
        {render()}
        {renderSheet()}
      </>
    );
  }

  return (
    <Column
      className={cn(
        "h-full min-h-0",
        variant === "mini" ? "w-72" : "w-fit shrink-0",
      )}
    >
      {render()}
      {renderSheet()}
    </Column>
  );
};

const CalendarPanelError = () => {
  const render = () => (
    <Center className="h-full min-h-0 min-w-0 flex-col gap-2 rounded-lg border border-lever-border-light bg-lever-surface-default p-6 text-center">
      <CalendarOffIcon className="h-6 w-6 text-lever-text-disabled" />
      <Text className="font-lever-suisse text-xs font-medium text-lever-text-label">
        Couldn&apos;t load the calender :(
      </Text>
    </Center>
  );

  return render();
};
